import React, {useState, useEffect, useCallback} from 'react';
import {Modal, Input, Row, Col, Spin, Button, Tabs, message} from 'antd';
import {SearchOutlined} from '@ant-design/icons';
import api from '../../api';
import {useTranslations} from '../../lang';
import './style.css';

export default function IconPickerModal({open, onClose, onSelect}) {
    const {translate} = useTranslations();
    const [icons, setIcons] = useState([]);
    const [filteredIcons, setFilteredIcons] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [freepikResults, setFreepikResults] = useState([]);
    const [freepikSearching, setFreepikSearching] = useState(false);
    const [freepikTerm, setFreepikTerm] = useState('');

    useEffect(() => {
        if (open) {
            setLoading(true);
            api.getAllIcons().then((data) => {
                setIcons(data);
                setFilteredIcons(data);
            }).finally(() => setLoading(false));
        }
    }, [open]);

    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredIcons(icons);
        } else {
            const term = searchTerm.toLowerCase();
            setFilteredIcons(icons.filter(icon =>
                icon.name.toLowerCase().includes(term)
            ));
        }
    }, [searchTerm, icons]);

    const handleFreepikSearch = useCallback(() => {
        if (!freepikTerm.trim()) return;
        setFreepikSearching(true);
        api.searchFreepikIconsUser(freepikTerm).then(setFreepikResults)
            .catch((err) => {
                console.error('FreePik search failed:', err);
                message.error('Zoeken mislukt. Probeer het opnieuw.');
                setFreepikResults([]);
            })
            .finally(() => setFreepikSearching(false));
    }, [freepikTerm]);

    const handleSelectFreepik = useCallback((result) => {
        // Create icon from FreePik and then select it
        api.createIconFromFreepikUser(result.name, result.thumbnail_url).then((icon) => {
            onSelect(icon);
        });
    }, [onSelect]);

    const handleClose = useCallback(() => {
        setSearchTerm('');
        setFreepikTerm('');
        setFreepikResults([]);
        onClose();
    }, [onClose]);

    const tabItems = [
        {
            key: '1',
            label: translate('Beschikbare iconen'),
            children: (
                <>
                    <Input
                        placeholder={translate('Zoek op naam...')}
                        prefix={<SearchOutlined/>}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="mar-b-1"
                    />
                    {loading ? (
                        <div className="text-center"><Spin/></div>
                    ) : (
                        <div className="icon-picker-grid">
                            {filteredIcons.map(icon => (
                                <div
                                    key={icon.id}
                                    className="icon-picker-item"
                                    onClick={() => onSelect(icon)}
                                >
                                    <img src={icon.url} alt={icon.name}/>
                                    <span>{icon.name}</span>
                                </div>
                            ))}
                            {filteredIcons.length === 0 && !loading && (
                                <p>{translate('Geen iconen gevonden')}</p>
                            )}
                        </div>
                    )}
                </>
            )
        },
        {
            key: '2',
            label: translate('Zoek nieuw icoon'),
            children: (
                <>
                    <Row gutter={8} className="mar-b-1">
                        <Col flex="auto">
                            <Input
                                placeholder={translate('Bijv. tomato, carrot, lettuce...')}
                                value={freepikTerm}
                                onChange={(e) => setFreepikTerm(e.target.value)}
                                onPressEnter={handleFreepikSearch}
                            />
                        </Col>
                        <Col>
                            <Button type="primary" onClick={handleFreepikSearch} loading={freepikSearching}>
                                {translate('Zoeken')}
                            </Button>
                        </Col>
                    </Row>
                    {freepikSearching ? (
                        <div className="text-center"><Spin/></div>
                    ) : (
                        <div className="icon-picker-grid">
                            {freepikResults.map(result => (
                                <div
                                    key={result.id}
                                    className="icon-picker-item"
                                    onClick={() => handleSelectFreepik(result)}
                                >
                                    <img src={result.thumbnail_url} alt={result.name}/>
                                    <span>{result.name}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )
        }
    ];

    return (
        <Modal
            open={open}
            title={translate('Kies een icoon')}
            onCancel={handleClose}
            footer={null}
            width={600}
        >
            <Tabs items={tabItems}/>
        </Modal>
    );
}
